
import { useState } from 'react';

interface CheckItem {
  id: string;
  label: string;
  category: string;
}

const checklistItems: CheckItem[] = [
  // 映像関連
  { id: 'video-1', label: 'カメラが正しく接続されている', category: '映像' },
  { id: 'video-2', label: 'カメラのレンズキャップが外れている', category: '映像' },
  { id: 'video-3', label: '配信ソフトでカメラが認識されている', category: '映像' },
  { id: 'video-4', label: '照明が適切に設定されている', category: '映像' },
  { id: 'video-5', label: '映像の解像度・フレームレートが適切', category: '映像' },
  // 音声関連
  { id: 'audio-1', label: 'マイクが正しく接続されている', category: '音声' },
  { id: 'audio-2', label: 'マイクがミュートになっていない', category: '音声' },
  { id: 'audio-3', label: '音声入力デバイスが正しく選択されている', category: '音声' },
  { id: 'audio-4', label: '音量レベルが適切（-12dB〜-6dB）', category: '音声' },
  { id: 'audio-5', label: 'ノイズ抑制・エコーキャンセルが有効', category: '音声' },
  // ネットワーク関連
  { id: 'network-1', label: 'インターネット接続が安定している', category: 'ネットワーク' },
  { id: 'network-2', label: '上り帯域が十分（推奨: 10Mbps以上）', category: 'ネットワーク' },
  { id: 'network-3', label: '有線LAN接続を使用している', category: 'ネットワーク' },
  { id: 'network-4', label: 'VPNを無効にしている', category: 'ネットワーク' },
  // ソフトウェア関連
  { id: 'software-1', label: '配信ソフトが最新版にアップデートされている', category: 'ソフトウェア' },
  { id: 'software-2', label: 'ストリームキー・URLが正しく設定されている', category: 'ソフトウェア' },
  { id: 'software-3', label: 'エンコーダー設定が適切', category: 'ソフトウェア' },
  { id: 'software-4', label: '不要なアプリケーションを終了している', category: 'ソフトウェア' },
];

const categories = ['映像', '音声', 'ネットワーク', 'ソフトウェア'];

export default function TroubleChecklist() {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleItem = (id: string) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(id)) {
      newChecked.delete(id);
    } else {
      newChecked.add(id);
    }
    setCheckedItems(newChecked);
  };

  const getCategoryProgress = (category: string) => {
    const categoryItems = checklistItems.filter(item => item.category === category);
    const checkedCount = categoryItems.filter(item => checkedItems.has(item.id)).length;
    return { checked: checkedCount, total: categoryItems.length };
  };

  const totalProgress = {
    checked: checkedItems.size,
    total: checklistItems.length
  };

  const progressPercentage = Math.round((totalProgress.checked / totalProgress.total) * 100);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case '映像': return 'ri-camera-line';
      case '音声': return 'ri-mic-line';
      case 'ネットワーク': return 'ri-wifi-line';
      case 'ソフトウェア': return 'ri-settings-3-line';
      default: return 'ri-checkbox-circle-line';
    }
  };

  const resetChecklist = () => {
    setCheckedItems(new Set());
  };

  return (
    <div className="my-8 bg-gradient-to-br from-cyan-50 to-teal-50 rounded-xl border border-cyan-200 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-cyan-500 to-teal-500 text-white cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
            <i className="ri-checkbox-multiple-line text-xl"></i>
          </div>
          <div className="text-left">
            <h3 className="font-bold text-lg">配信前チェックリスト</h3>
            <p className="text-cyan-100 text-sm">配信開始前に確認すべき項目</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <span className="text-2xl font-bold">{progressPercentage}%</span>
            <span className="text-cyan-100 text-sm ml-1">完了</span>
          </div>
          <div className={`w-6 h-6 flex items-center justify-center transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
            <i className="ri-arrow-down-s-line text-xl"></i>
          </div>
        </div>
      </button>

      <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[2000px]' : 'max-h-0'}`}>
        {/* 進捗バー */}
        <div className="px-6 pt-4">
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>{totalProgress.checked} / {totalProgress.total} 項目完了</span>
            {totalProgress.checked > 0 && (
              <button 
                onClick={resetChecklist}
                className="text-cyan-600 hover:text-cyan-700 cursor-pointer flex items-center gap-1"
              >
                <i className="ri-refresh-line"></i>
                リセット
              </button>
            )}
          </div>
        </div>

        {/* カテゴリ別チェックリスト */}
        <div className="p-6 space-y-4">
          {categories.map(category => {
            const progress = getCategoryProgress(category);
            const isComplete = progress.checked === progress.total;
            
            return (
              <div key={category} className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${isComplete ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-500'}`}>
                      <i className={getCategoryIcon(category)}></i>
                    </div>
                    <span className="font-semibold text-gray-900">{category}</span>
                  </div>
                  <span className={`text-sm font-medium ${isComplete ? 'text-teal-600' : 'text-gray-500'}`}>
                    {progress.checked}/{progress.total}
                    {isComplete && <i className="ri-check-line ml-1"></i>}
                  </span>
                </div>
                <div className="space-y-2">
                  {checklistItems
                    .filter(item => item.category === category)
                    .map(item => (
                      <label
                        key={item.id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
                      >
                        <div className={`w-5 h-5 flex items-center justify-center rounded border-2 transition-all ${
                          checkedItems.has(item.id) 
                            ? 'bg-teal-500 border-teal-500 text-white' 
                            : 'border-gray-300 group-hover:border-cyan-400'
                        }`}>
                          {checkedItems.has(item.id) && <i className="ri-check-line text-sm"></i>}
                        </div>
                        <input
                          type="checkbox"
                          checked={checkedItems.has(item.id)}
                          onChange={() => toggleItem(item.id)}
                          className="sr-only"
                        />
                        <span className={`text-sm ${checkedItems.has(item.id) ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                          {item.label}
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* 完了メッセージ */}
        {progressPercentage === 100 && (
          <div className="mx-6 mb-6 p-4 bg-teal-100 rounded-lg flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-500 text-white rounded-full">
              <i className="ri-check-double-line text-xl"></i>
            </div>
            <div>
              <p className="font-semibold text-teal-800">準備完了！</p>
              <p className="text-sm text-teal-700">すべてのチェック項目が完了しました。配信を開始できます。</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
